import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import { Copy, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  InsightsRangePicker, InsightsHistogram, InsightsObjectionsRow,
  INSIGHTS_SENT, INSIGHTS_INT,
} from "./insights";
import { CampaignCompareDialog } from "./compare";

function MixBar({ counts, total, order, palette }) {
  if (!total) return <div className="h-1.5 w-full rounded-full bg-slate-100" />;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      {order.map((k) => {
        const pct = (counts[k] || 0) / total * 100;
        if (!pct) return null;
        return (
          <div key={k} className={palette[k].bg} style={{ width: `${pct}%` }}
               title={`${palette[k].label}: ${counts[k]}`} />
        );
      })}
    </div>
  );
}

export function CrossCampaignInsightsPanel({ onOpenCampaign }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = usePersistedState("ai-outbound-cross.range", "30d");
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/outbound/insights?since=${range}`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [range]);

  if (loading) {
    return (
      <Card className="border-slate-200 p-6 text-center text-xs text-slate-400" data-testid="ai-outbound-cross-insights-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        <div className="mt-2">Loading insights…</div>
      </Card>
    );
  }
  if (!data || !data.total_scored) {
    return (
      <Card className="border-slate-200 p-8 text-center" data-testid="ai-outbound-cross-insights-empty">
        <Sparkles className="mx-auto h-6 w-6 text-slate-300" />
        <div className="mt-2 text-sm font-medium text-slate-600">No scored calls yet</div>
        <div className="mt-1 text-xs text-slate-400">
          Once your campaigns place calls and Whisper transcribes them, sentiment scoring runs automatically and shows up here.
        </div>
      </Card>
    );
  }

  const summary = [
    { key: "positive", ...INSIGHTS_SENT.positive, count: data.sentiment_counts.positive },
    { key: "warm",     ...INSIGHTS_INT.warm,     count: data.interest_counts.warm },
    { key: "hot",      ...INSIGHTS_INT.hot,      count: data.interest_counts.hot },
    { key: "negative", ...INSIGHTS_SENT.negative, count: data.sentiment_counts.negative },
  ];

  return (
    <Card className="border-slate-200 p-4" data-testid="ai-outbound-cross-insights">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Cross-Campaign Insights</h3>
        </div>
        <div className="flex items-center gap-2">
          <InsightsRangePicker value={range} onChange={setRange} testid="ai-outbound-cross-insights-range" />
          <Button variant="outline" size="sm" onClick={() => setCompareOpen(true)}
                  data-testid="ai-outbound-cross-compare-btn"
                  disabled={(data.per_campaign?.length || 0) < 2}
                  title={(data.per_campaign?.length || 0) < 2 ? "Need at least 2 campaigns with scored calls to compare" : "Compare two campaigns side-by-side"}>
            <Copy className="h-3.5 w-3.5" /> Compare
          </Button>
          <span className="text-[11px] text-slate-400" data-testid="ai-outbound-cross-insights-count">
            {data.total_scored} scored · {data.total_calls} total
          </span>
        </div>
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        {summary.map((s) => (
          <div key={s.key} className="rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`ai-outbound-cross-kpi-${s.key}`}>
            <div className={`text-[10px] uppercase tracking-wide ${s.text}`}>{s.label}</div>
            <div className="mt-0.5 text-xl font-semibold text-slate-800 tabular-nums">{s.count}</div>
            <div className="text-[10px] text-slate-400">
              {data.total_scored ? Math.round((s.count / data.total_scored) * 100) : 0}%
            </div>
          </div>
        ))}
      </div>

      <InsightsHistogram data={data} onDrill={null} />
      <InsightsObjectionsRow data={data} onDrill={null} />

      {data.per_campaign?.length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
            Per-campaign breakdown
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-xs" data-testid="ai-outbound-cross-per-campaign">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-1.5 text-left">Campaign</th>
                  <th className="px-3 py-1.5 text-right">Scored</th>
                  <th className="px-3 py-1.5 text-left w-64">Sentiment mix</th>
                  <th className="px-3 py-1.5 text-left w-64">Interest mix</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.per_campaign.map((p) => (
                  <tr key={p.campaign_id}
                      data-testid={`ai-outbound-cross-row-${p.campaign_id}`}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => onOpenCampaign?.(p.campaign_id)}>
                    <td className="px-3 py-1.5 font-medium text-slate-700">{p.campaign_name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{p.total_scored}</td>
                    <td className="px-3 py-1.5">
                      <MixBar counts={p.sentiment_counts} total={p.total_scored}
                              order={["positive", "neutral", "negative"]} palette={INSIGHTS_SENT} />
                    </td>
                    <td className="px-3 py-1.5">
                      <MixBar counts={p.interest_counts} total={p.total_scored}
                              order={["hot", "warm", "cold"]} palette={INSIGHTS_INT} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CampaignCompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        campaigns={data.per_campaign || []}
        since={range}
      />
    </Card>
  );
}
